/**
 * ProfileSection — Ayarlar > Profil
 *
 * Kullanıcının kişisel bilgilerini düzenleyebildiği panel. Email ve rol
 * server-controlled (readonly) — email kimlik, rol yalnızca super_admin
 * flow'undan değişir. İsim, ünvan, telefon ve timezone kullanıcıya açık.
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import { COMMON_TIMEZONES } from "../../lib/dates";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { User, Mail, Phone, Briefcase, Globe, Shield, Camera, Trash2 } from "lucide-react";

const ROLE_LABELS = {
  platform_admin: "Platform Admin",
  super_admin: "Süper Yönetici",
  admin: "Yönetici",
  user: "Kullanıcı",
};

function Avatar({ name, photoUrl }) {
  const initials =
    (name || "?")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0])
      .join("")
      .toUpperCase() || "?";
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt="profil"
        className="h-16 w-16 shrink-0 rounded-full object-cover shadow-md ring-2 ring-white"
      />
    );
  }
  return (
    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-zinc-800 to-zinc-950 text-lg font-semibold text-white shadow-md">
      {initials}
    </div>
  );
}

export default function ProfileSection() {
  const { user, setUser } = useAuthStore();
  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    title: user?.title || "",
    timezone: user?.timezone || "UTC",
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const photoSrc = user?.photo_url || null;

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!f) return;
    if (!/^image\/(jpeg|jpg|png|webp|gif)$/i.test(f.type)) {
      toast.error("Sadece JPG, PNG, WebP veya GIF yükleyebilirsin");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Dosya 5 MB'ı geçemez");
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/users/me/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser?.({ ...user, photo_url: data.photo_url });
      toast.success("Fotoğraf güncellendi");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Yüklenemedi",
      );
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    if (!window.confirm("Profil fotoğrafı kaldırılsın mı?")) return;
    try {
      await api.delete("/users/me/photo");
      setUser?.({ ...user, photo_url: null });
      toast.success("Fotoğraf kaldırıldı");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const dirty =
    form.name !== (user?.name || "") ||
    form.phone !== (user?.phone || "") ||
    form.title !== (user?.title || "") ||
    form.timezone !== (user?.timezone || "UTC");

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me/profile", form);
      setUser?.({ ...user, ...data });
      toast.success("Profil güncellendi");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Kaydedilemedi",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      className="max-w-3xl space-y-6 rounded-lg border border-zinc-200 bg-white p-6"
      data-testid="profile-section"
    >
      <div className="flex items-center gap-2 border-b border-zinc-100 pb-3">
        <User className="h-4 w-4 text-zinc-500" strokeWidth={2} />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-700">
          Profil
        </h2>
      </div>
      <div className="flex items-center gap-4">
        <div className="relative">
          <Avatar name={form.name || user?.email} photoUrl={photoSrc} />
          <button
            type="button"
            onClick={onPickFile}
            disabled={uploadingPhoto}
            data-testid="profile-photo-upload-btn"
            title="Fotoğraf yükle"
            className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-zinc-900 text-white shadow-md ring-2 ring-white transition-transform hover:scale-105 disabled:opacity-60"
          >
            <Camera className="h-3.5 w-3.5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onFileChange}
            data-testid="profile-photo-input"
            className="hidden"
          />
        </div>
        <div className="min-w-0">
          <div className="truncate text-lg font-semibold text-zinc-950">
            {form.name || user?.email}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-sm text-zinc-500">
            <Mail className="h-3.5 w-3.5" />
            <span className="truncate">{user?.email}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <div className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-700">
              <Shield className="h-3 w-3" />
              {ROLE_LABELS[user?.role] || user?.role}
            </div>
            {photoSrc && (
              <button
                type="button"
                onClick={removePhoto}
                data-testid="profile-photo-remove-btn"
                className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700 hover:bg-rose-100"
              >
                <Trash2 className="h-3 w-3" />
                Fotoğrafı kaldır
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label className="flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500">
            <User className="h-3 w-3" /> Ad Soyad
          </Label>
          <Input
            data-testid="profile-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Adın Soyadın"
            maxLength={80}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500">
            <Briefcase className="h-3 w-3" /> Ünvan
          </Label>
          <Input
            data-testid="profile-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="ör. Proje Yöneticisi"
            maxLength={80}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500">
            <Phone className="h-3 w-3" /> Telefon
          </Label>
          <Input
            data-testid="profile-phone-input"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="+90 555 123 45 67"
            maxLength={40}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="flex items-center gap-1 text-xs uppercase tracking-wider text-zinc-500">
            <Globe className="h-3 w-3" /> Saat dilimi
          </Label>
          <Select
            value={form.timezone}
            onValueChange={(v) => setForm({ ...form, timezone: v })}
          >
            <SelectTrigger
              data-testid="profile-timezone-select"
              className="mt-1"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMMON_TIMEZONES.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-zinc-100 pt-4">
        <p className="text-xs text-zinc-500">
          E-posta ve rol yönetici tarafından değiştirilir.
        </p>
        <Button
          type="submit"
          disabled={saving || !dirty}
          data-testid="profile-save-btn"
          className="bg-zinc-900 text-white hover:bg-zinc-800"
        >
          {saving ? "Kaydediliyor..." : "Değişiklikleri kaydet"}
        </Button>
      </div>
    </form>
  );
}
